import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { api } from "../../api/api";
import { commercialProposalApi } from "../../api/commercialProposalApi";
import {
  CommercialProposalStatus,
  IProposalMeta,
  IProposalProduceOption,
  ProposalCurrency,
} from "../../types/commercialProposal.type";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Text } = Typography;

/** Роли, которым лимит скидки не применяется, пока не включён отдельный тумблер. */
const PROPOSAL_ADMIN_ROLES: Array<UserRole | string> = [
  UserRole.Admin,
  UserRole.Director,
  UserRole.Manager,
];

interface ClientOption {
  _id: string;
  name: string;
  phone?: string;
}

interface FormItem {
  key: string;
  produceId?: string;
  quantity: number;
  discountPercent: number;
}

let rowKeyCounter = 0;
const nextRowKey = () => `row-${++rowKeyCounter}`;

const formatMoney = (value: number, currency: string) =>
  `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value)} ${currency}`;

/**
 * Предварительный расчёт срока производства позиции (backend — источник истины):
 * нужно_произвести = max(0, очередь + количество − остаток); срок = ceil(need / производительность).
 * Возвращает null, если производительность не задана.
 */
/** Пересчёт процента скидки из введённой цены со скидкой (0..100, 2 знака). */
function priceToDiscount(priceDiscounted: number, basePrice: number): number {
  if (!basePrice || basePrice <= 0) return 0;
  const discount = (1 - priceDiscounted / basePrice) * 100;
  const clamped = Math.min(100, Math.max(0, discount));
  return Math.round(clamped * 100) / 100;
}

function calcItemLeadTime(
  quantity: number,
  queue: number,
  residue: number,
  capacity: number | null
): number | null {
  const need = Math.max(0, queue + quantity - residue);
  if (need <= 0) return 0;
  if (!capacity || capacity <= 0) return null;
  return Math.ceil(need / capacity);
}

export default function CommercialProposalFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [initializing, setInitializing] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<IProposalMeta | null>(null);
  const [editStatus, setEditStatus] = useState<CommercialProposalStatus | null>(null);

  // Лимит скидки применяется к текущему пользователю? Повторяет правило бэкенда:
  // общий тумблер, а для админских ролей — ещё и отдельный тумблер для них.
  const { authState } = useAuth();
  const discountLimitApplies =
    meta?.discountLimit !== false &&
    (!PROPOSAL_ADMIN_ROLES.includes(authState.user?.role ?? "") ||
      meta?.adminDiscountLimit === true);

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>();
  const clientSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const [produceOptions, setProduceOptions] = useState<IProposalProduceOption[]>([]);
  const [producesLoading, setProducesLoading] = useState(false);
  const produceSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  // Кеш выбранных продукций, чтобы строки не «теряли» данные при поиске
  const produceCache = useRef<Map<string, IProposalProduceOption>>(new Map());

  // Валюта КП всегда UZS (по бизнес-требованию)
  const currency: ProposalCurrency = "UZS";
  const [items, setItems] = useState<FormItem[]>([
    { key: nextRowKey(), quantity: 1, discountPercent: 0 },
  ]);
  const [dirty, setDirty] = useState(false);
  const [addingAll, setAddingAll] = useState(false);
  // Ручная корректировка срока выполнения (только в бо́льшую сторону)
  const [leadOverride, setLeadOverride] = useState<number | null>(null);

  /* ===== Загрузка данных ===== */

  const fetchClients = useCallback(async (search?: string) => {
    setClientsLoading(true);
    try {
      const { data } = await api.get("/client", {
        params: { search: search || undefined, limit: 20, page: 1 },
      });
      const list = data.clients || data;
      setClients(
        (Array.isArray(list) ? list : []).map((c: any) => ({
          _id: c._id,
          name: c.name,
          phone: c.phone,
        }))
      );
    } catch {
      message.error("Ошибка при загрузке клиентов");
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const fetchProduces = useCallback(async (search?: string) => {
    setProducesLoading(true);
    try {
      const { data } = await commercialProposalApi.getProduceOptions({
        search: search || undefined,
        limit: 100,
      });
      const options: IProposalProduceOption[] = data.produces || [];
      options.forEach((option) => produceCache.current.set(option._id, option));
      setProduceOptions(options);
    } catch {
      message.error("Ошибка при загрузке продукции");
    } finally {
      setProducesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchProduces();
    commercialProposalApi
      .getMeta()
      .then(({ data }) => setMeta(data))
      .catch(() => {
        // Настройки не критичны для формы
      });
  }, [fetchClients, fetchProduces]);

  // Режим редактирования: загружаем КП
  useEffect(() => {
    if (!id) return;
    commercialProposalApi
      .getById(id)
      .then(async ({ data }) => {
        const proposal = data.proposal;
        setEditStatus(proposal.status);
        setSelectedClientId(proposal.clientId);
        setClients((prev) =>
          prev.some((c) => c._id === proposal.clientId)
            ? prev
            : [{ _id: proposal.clientId, name: proposal.clientName, phone: proposal.clientPhone }, ...prev]
        );
        // Подтягиваем актуальные данные по продукциям позиций
        const produceIds: string[] = proposal.items.map((i: any) => i.produceId);
        const missing = produceIds.filter((pid) => !produceCache.current.has(pid));
        if (missing.length > 0) {
          try {
            const { data: produceData } = await commercialProposalApi.getProduceOptions({ limit: 200 });
            (produceData.produces || []).forEach((option: IProposalProduceOption) =>
              produceCache.current.set(option._id, option)
            );
          } catch {
            // Данные по ценам подтянутся при поиске
          }
        }
        setItems(
          proposal.items.map((item: any) => ({
            key: nextRowKey(),
            produceId: item.produceId,
            quantity: item.quantity,
            discountPercent: item.discountPercent,
          }))
        );
        // Восстанавливаем ручную корректировку срока, если она была
        if (proposal.leadTimeManual) {
          setLeadOverride(proposal.leadTimeDays);
        }
      })
      .catch((error: any) => {
        message.error(error?.response?.data?.error || "Ошибка при загрузке КП");
        navigate("/commercial-proposals");
      })
      .finally(() => setInitializing(false));
  }, [id, navigate]);

  /* ===== Поиск ===== */

  const handleClientSearch = (value: string) => {
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => fetchClients(value), 400);
  };

  const handleProduceSearch = (value: string) => {
    clearTimeout(produceSearchTimer.current);
    produceSearchTimer.current = setTimeout(() => fetchProduces(value), 400);
  };

  /* ===== Работа со строками ===== */

  const updateItem = (key: string, patch: Partial<FormItem>) => {
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
    setDirty(true);
  };

  const addItem = () => {
    setItems((prev) => [...prev, { key: nextRowKey(), quantity: 1, discountPercent: 0 }]);
    setDirty(true);
  };

  const removeItem = (key: string) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((item) => item.key !== key) : prev));
    setDirty(true);
  };

  /** Добавляет в КП весь каталог продукции (типовой сценарий). */
  const addAllProduces = async () => {
    setAddingAll(true);
    try {
      const { data } = await commercialProposalApi.getProduceOptions({ limit: 1000 });
      const options: IProposalProduceOption[] = data.produces || [];
      options.forEach((option) => produceCache.current.set(option._id, option));
      setProduceOptions(options);
      let addedCount = 0;
      setItems((prev) => {
        const existing = prev.filter((item) => item.produceId);
        const existingIds = new Set(existing.map((item) => item.produceId));
        const added = options
          .filter((option) => !existingIds.has(option._id))
          .map((option) => ({
            key: nextRowKey(),
            produceId: option._id,
            quantity: 1,
            discountPercent: 0,
          }));
        addedCount = added.length;
        const merged = [...existing, ...added];
        return merged.length > 0 ? merged : prev;
      });
      setDirty(true);
      message.success(
        addedCount > 0 ? `Добавлено товаров: ${addedCount}` : "Все товары уже добавлены"
      );
    } catch {
      message.error("Ошибка при загрузке каталога продукции");
    } finally {
      setAddingAll(false);
    }
  };

  const getProduceInfo = (produceId?: string) => {
    if (!produceId) return null;
    const option = produceCache.current.get(produceId);
    if (!option) return null;
    return { option, info: option[currency] };
  };

  /* ===== Расчёты (предварительные, сервер пересчитает) ===== */

  const computedRows = useMemo(() => {
    return items.map((item) => {
      const produce = getProduceInfo(item.produceId);
      const basePrice = produce?.info.basePrice || 0;
      const minPrice = produce?.info.minPrice || 0;
      // Потолок уступки уже посчитан на бэкенде: минимум из макс. скидки
      // категории и скидки до мин. цены.
      const maxDiscount = produce?.info.maxDiscountPercent ?? 100;
      const categoryMaxDiscount = produce?.info.categoryMaxDiscountPercent ?? null;
      const priceDiscounted = Math.round(basePrice * ((100 - item.discountPercent) / 100) * 100) / 100;
      const lineTotal = Math.round(priceDiscounted * item.quantity * 100) / 100;
      // Лимит скидки можно отключить в настройках, а админским ролям он не
      // применяется, пока не включён отдельный тумблер — тогда не предупреждаем.
      // Скидка не проходит, если она больше потолка категории ИЛИ уводит цену
      // ниже мин. цены — те же два правила, что на бэкенде.
      const discountTooBig =
        discountLimitApplies &&
        Boolean(produce) &&
        item.discountPercent > 0 &&
        (priceDiscounted < minPrice - 1e-9 ||
          (categoryMaxDiscount != null && item.discountPercent > categoryMaxDiscount));
      // Срок производства позиции (превью)
      const capacity = produce?.option.dailyCapacity ?? null;
      const leadTime = produce
        ? calcItemLeadTime(item.quantity || 0, produce.option.queue || 0, produce.option.residue || 0, capacity)
        : null;
      return {
        ...item,
        basePrice,
        minPrice,
        maxDiscount,
        priceDiscounted,
        lineTotal,
        discountTooBig,
        capacity,
        queue: produce?.option.queue ?? 0,
        residue: produce?.option.residue ?? 0,
        leadTime,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, currency, produceOptions, discountLimitApplies]);

  const totals = useMemo(() => {
    const withProduce = computedRows.filter((row) => row.produceId);
    const totalBase = withProduce.reduce((sum, row) => sum + row.basePrice * row.quantity, 0);
    const totalAmount = withProduce.reduce((sum, row) => sum + row.lineTotal, 0);
    return { totalBase, totalAmount, totalDiscount: totalBase - totalAmount };
  }, [computedRows]);

  // Итоговый срок выполнения = максимум по позициям + процент страховки из настроек;
  // ручная правка — только больше
  const leadTimeRaw = useMemo(
    () =>
      computedRows
        .filter((row) => row.produceId)
        .reduce((max, row) => Math.max(max, row.leadTime || 0), 0),
    [computedRows]
  );
  const safetyPercent = meta?.leadTimeSafetyPercent || 0;
  const leadTimeAuto = useMemo(
    () => (leadTimeRaw > 0 && safetyPercent > 0 ? Math.ceil(leadTimeRaw * (1 + safetyPercent / 100)) : leadTimeRaw),
    [leadTimeRaw, safetyPercent]
  );
  const effectiveLeadTime =
    leadOverride !== null && leadOverride > leadTimeAuto ? leadOverride : leadTimeAuto;
  const hasCapacityGap = computedRows.some((row) => row.produceId && row.leadTime === null);

  // Что увидит клиент в документе: при авторасчёте — вилка −/+50%, при ручной
  // правке — точный срок. Должно совпадать с formatLeadTimeText на backend.
  const isLeadManual = leadOverride !== null && leadOverride > leadTimeAuto;
  const leadTimeRange = useMemo(() => {
    if (effectiveLeadTime <= 0) return null;
    const from = Math.max(1, Math.ceil(effectiveLeadTime * 0.5));
    const to = Math.ceil(effectiveLeadTime * 1.5);
    return { from, to: Math.max(from, to) };
  }, [effectiveLeadTime]);

  const hasInvalidDiscount = computedRows.some((row) => row.discountTooBig);
  const selectedClient = clients.find((c) => c._id === selectedClientId);

  /* ===== Сохранение ===== */

  const validate = (): string | null => {
    if (!selectedClientId) return "Выберите клиента.";
    const filled = items.filter((item) => item.produceId);
    if (filled.length === 0) return "Добавьте хотя бы одну позицию.";
    if (items.some((item) => item.produceId && (!item.quantity || item.quantity <= 0)))
      return "Количество должно быть больше нуля.";
    if (hasInvalidDiscount)
      return "Скидка по одной или нескольким позициям превышает допустимую.";
    return null;
  };

  const save = async () => {
    const error = validate();
    if (error) {
      message.error(error);
      return;
    }

    const payload = {
      clientId: selectedClientId!,
      currency,
      items: items
        .filter((item) => item.produceId)
        .map((item) => ({
          produceId: item.produceId!,
          quantity: item.quantity,
          discountPercent: item.discountPercent,
        })),
      leadTimeDays: effectiveLeadTime,
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        const { data } = await commercialProposalApi.update(id, payload);
        message.success("КП обновлено");
        navigate(`/commercial-proposals/${data.proposal._id}`);
      } else {
        const { data } = await commercialProposalApi.create(payload);
        message.success("КП создано");
        navigate(`/commercial-proposals/${data.proposal._id}`);
      }
      setDirty(false);
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Ошибка при сохранении КП");
    } finally {
      setSaving(false);
    }
  };

  // Подтверждение перед созданием/сохранением — сводка для проверки
  const confirmAndSave = () => {
    const error = validate();
    if (error) {
      message.error(error);
      return;
    }
    const filledCount = items.filter((item) => item.produceId).length;
    Modal.confirm({
      title: isEdit ? "Сохранить изменения КП?" : "Создать коммерческое предложение?",
      width: 460,
      content: (
        <div style={{ fontSize: 14, lineHeight: 1.9 }}>
          <div>
            Клиент: <b>{selectedClient?.name || "—"}</b>
          </div>
          <div>
            Позиций: <b>{filledCount}</b>
          </div>
          <div>
            Итого: <b>{formatMoney(totals.totalAmount, currency)}</b>
            {totals.totalDiscount > 0.004 && (
              <span> (скидка {formatMoney(totals.totalDiscount, currency)})</span>
            )}
          </div>
          <div>
            Срок выполнения: <b>{effectiveLeadTime} дн.</b>
          </div>
          <div style={{ marginTop: 8, color: "#8c8c8c" }}>
            Проверьте, что все данные указаны верно.
            {!isEdit && " После создания КP будет отправлено в WhatsApp-группу."}
          </div>
        </div>
      ),
      okText: isEdit ? "Сохранить" : "Создать КП",
      cancelText: "Проверить ещё раз",
      onOk: () => save(),
    });
  };

  const handleBack = () => {
    if (dirty) {
      Modal.confirm({
        title: "Есть несохранённые изменения",
        content: "Выйти без сохранения?",
        okText: "Выйти",
        cancelText: "Остаться",
        onOk: () => navigate("/commercial-proposals"),
      });
    } else {
      navigate("/commercial-proposals");
    }
  };

  /* ===== Колонки таблицы позиций ===== */

  const columns: ColumnsType<(typeof computedRows)[number]> = [
    {
      title: "Продукция",
      key: "produce",
      width: 320,
      render: (_, row) => (
        <Select
          showSearch
          placeholder="Выберите продукцию"
          value={row.produceId}
          style={{ width: "100%" }}
          filterOption={false}
          onSearch={handleProduceSearch}
          loading={producesLoading}
          notFoundContent={producesLoading ? <Spin size="small" /> : "Ничего не найдено"}
          onChange={(value) => updateItem(row.key, { produceId: value })}
          options={produceOptions.map((option) => ({
            value: option._id,
            label: option.name,
          }))}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Текущая цена / мин. цена (ниже неё нельзя) / максимальная скидка">
          <span>Цена / Мин. / Макс. скидка</span>
        </Tooltip>
      ),
      key: "priceInfo",
      width: 220,
      render: (_, row) => {
        if (!row.produceId) return <Text type="secondary">—</Text>;
        if (!row.basePrice)
          return <Text type="danger">Нет цены ({currency})</Text>;
        return (
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div>
              Цена: <b>{formatMoney(row.basePrice, currency)}</b>
            </div>
            <div>
              Мин: <b>{formatMoney(row.minPrice, currency)}</b> · Скидка до{" "}
              <b>{row.maxDiscount}%</b>
            </div>
          </div>
        );
      },
    },
    {
      title: "Кол-во",
      key: "quantity",
      width: 120,
      render: (_, row) => (
        <InputNumber
          min={0.001}
          value={row.quantity}
          style={{ width: "100%" }}
          onChange={(value) => updateItem(row.key, { quantity: Number(value) || 0 })}
        />
      ),
    },
    {
      title: "Скидка, %",
      key: "discount",
      width: 130,
      render: (_, row) => (
        <div>
          <InputNumber
            min={0}
            max={100}
            value={row.discountPercent}
            style={{ width: "100%" }}
            status={row.discountTooBig ? "error" : undefined}
            onChange={(value) => updateItem(row.key, { discountPercent: Number(value) || 0 })}
          />
          {row.discountTooBig && (
            <Text type="danger" style={{ fontSize: 11 }}>
              Макс. {row.maxDiscount}%
            </Text>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Можно ввести цену со скидкой — процент скидки пересчитается автоматически">
          <span>Цена со скидкой</span>
        </Tooltip>
      ),
      key: "priceDiscounted",
      align: "right",
      width: 160,
      render: (_, row) =>
        row.produceId ? (
          <InputNumber
            // key: ремонт при изменении скидки извне (поле «Скидка, %»), чтобы
            // показать пересчитанную цену; ввод — неконтролируемый (не мешает печатать)
            key={`${row.key}-${row.discountPercent}`}
            min={0}
            defaultValue={row.priceDiscounted}
            style={{ width: "100%" }}
            status={row.discountTooBig ? "error" : undefined}
            disabled={!row.basePrice}
            controls={false}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
            parser={(value) => Number((value || "").replace(/\s/g, ""))}
            onBlur={(e) => {
              const price = Number((e.target.value || "").replace(/\s/g, "")) || 0;
              updateItem(row.key, {
                discountPercent: priceToDiscount(price, row.basePrice),
              });
            }}
          />
        ) : (
          "—"
        ),
    },
    {
      title: (
        <Tooltip title="Срок производства позиции с учётом очереди заказов и остатка на складе">
          <span>Срок</span>
        </Tooltip>
      ),
      key: "leadTime",
      align: "center",
      width: 110,
      render: (_, row) => {
        if (!row.produceId) return "—";
        if (row.leadTime === null)
          return (
            <Tooltip title="Не задана производительность (у товара или в настройках)">
              <Text type="warning" style={{ fontSize: 12 }}>
                не задан
              </Text>
            </Tooltip>
          );
        return (
          <Text style={{ fontSize: 12 }}>
            {row.leadTime === 0 ? "со склада" : `${row.leadTime} дн.`}
          </Text>
        );
      },
    },
    {
      title: "Сумма",
      key: "lineTotal",
      align: "right",
      width: 150,
      render: (_, row) => (row.produceId ? <b>{formatMoney(row.lineTotal, currency)}</b> : "—"),
    },
    {
      title: "",
      key: "remove",
      width: 50,
      render: (_, row) => (
        <Button
          size="small"
          danger
          type="text"
          icon={<DeleteOutlined />}
          disabled={items.length === 1}
          onClick={() => removeItem(row.key)}
        />
      ),
    },
  ];

  if (initializing) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Назад
        </Button>
        <h2 style={{ margin: 0 }}>
          {isEdit ? "Редактирование КП" : "Создание коммерческого предложения"}
        </h2>
      </Space>

      {isEdit && editStatus === CommercialProposalStatus.Created && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Редактирование созданного КП увеличит его версию. Цены будут пересчитаны по актуальным данным."
        />
      )}

      <Card title="Шаг 1 — Клиент" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Select
              showSearch
              placeholder="Выберите клиента"
              value={selectedClientId}
              style={{ width: "100%" }}
              filterOption={false}
              onSearch={handleClientSearch}
              loading={clientsLoading}
              notFoundContent={clientsLoading ? <Spin size="small" /> : "Клиент не найден"}
              onChange={(value) => {
                setSelectedClientId(value);
                setDirty(true);
              }}
              options={clients.map((client) => ({ value: client._id, label: client.name }))}
            />
          </Col>
          <Col xs={24} md={14}>
            {selectedClient && (
              <Text type="secondary">
                Телефон: {selectedClient.phone || "не указан"}
              </Text>
            )}
          </Col>
        </Row>
      </Card>

      <Card
        title="Шаг 2 — Продукция"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button type="primary" ghost loading={addingAll} onClick={addAllProduces}>
              Добавить все товары
            </Button>
            <Button icon={<PlusOutlined />} onClick={addItem}>
              Добавить товар
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="key"
          columns={columns}
          dataSource={computedRows}
          pagination={false}
          size="small"
          virtual={computedRows.length > 25}
          scroll={computedRows.length > 25 ? { x: 1050, y: 520 } : { x: 1050 }}
          locale={{ emptyText: "Добавьте товары" }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <div style={{ minWidth: 300 }}>
            {totals.totalDiscount > 0.004 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Сумма без скидки</Text>
                  <Text>{formatMoney(totals.totalBase, currency)}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Скидка</Text>
                  <Text type="success">− {formatMoney(totals.totalDiscount, currency)}</Text>
                </div>
              </>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 18,
                fontWeight: 600,
                borderTop: "2px solid #16a34a",
                marginTop: 8,
                paddingTop: 8,
              }}
            >
              <span>Итого</span>
              <span>{formatMoney(totals.totalAmount, currency)}</span>
            </div>

            {/* Срок выполнения */}
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong>Срок выполнения</Text>
                <InputNumber
                  min={leadTimeAuto}
                  value={effectiveLeadTime}
                  addonAfter="дн."
                  style={{ width: 130 }}
                  onChange={(value) => {
                    const v = Number(value) || 0;
                    setLeadOverride(v > leadTimeAuto ? v : null);
                    setDirty(true);
                  }}
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                Расчёт по загрузке производства: {leadTimeRaw} дн.
                {safetyPercent > 0 && ` + страховка ${safetyPercent}% = ${leadTimeAuto} дн.`}
                {" "}Можно только увеличить.
              </Text>
              {leadTimeRange && (
                <Text style={{ fontSize: 12, display: "block", marginTop: 4, color: "#08979c" }}>
                  В документе:{" "}
                  <b>
                    {isLeadManual || leadTimeRange.from === leadTimeRange.to
                      ? `срок выполнения заказа — ${effectiveLeadTime} дн.`
                      : `срок выполнения заказа — от ${leadTimeRange.from} до ${leadTimeRange.to} дн.`}
                  </b>
                </Text>
              )}
              {hasCapacityGap && (
                <Text type="warning" style={{ fontSize: 12, display: "block" }}>
                  У части позиций не задана производительность — их срок не учтён.
                </Text>
              )}
              {meta && (
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                  Срок действия КП: {meta.validityDays} дн. (из настроек)
                </Text>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Space>
        <Button
          type="primary"
          size="large"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={hasInvalidDiscount}
          onClick={confirmAndSave}
        >
          {isEdit ? "Сохранить КП" : "Создать КП"}
        </Button>
        <Button size="large" onClick={handleBack}>
          Отмена
        </Button>
      </Space>
    </div>
  );
}
